var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/referral.ts
import { AnchorProvider, Program } from "@coral-xyz/anchor";
import {
  AccountLayout,
  createAssociatedTokenAccountIdempotentInstruction,
  getAssociatedTokenAddressSync as getAssociatedTokenAddressSync2,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID as TOKEN_PROGRAM_ID2
} from "@solana/spl-token";
import {
  ComputeBudgetProgram as ComputeBudgetProgram2,
  PublicKey as PublicKey3,
  TransactionMessage as TransactionMessage2,
  VersionedTransaction as VersionedTransaction2
} from "@solana/web3.js";
import chunk from "lodash/chunk";

// src/chunks.ts
function chunks(array, size) {
  return Array.apply(0, new Array(Math.ceil(array.length / size))).map(
    (_, index) => array.slice(index * size, (index + 1) * size)
  );
}
function chunkedGetMultipleAccountInfos(connection, pks, chunkSize = 100) {
  return __async(this, null, function* () {
    return (yield Promise.all(
      chunks(pks, chunkSize).map(
        (chunk2) => connection.getMultipleAccountsInfo(chunk2)
      )
    )).flat();
  });
}

// src/constant.ts
import { clusterApiUrl, PublicKey } from "@solana/web3.js";
var PROGRAM_ID = new PublicKey(
  "REFER4ZgmyYx9c6He5XfaTMiGfdLwRnkV4RPp9t9iF3"
);
var RPC_URL = process.env.RPC_URL || process.env.NEXT_PUBLIC_RPC_URL || clusterApiUrl("mainnet-beta");

// src/FeeService.ts
import {
  ComputeBudgetProgram,
  Connection,
  PublicKey as PublicKey2,
  TransactionMessage,
  VersionedTransaction
} from "@solana/web3.js";
var FeeServiceImpl = class {
  constructor() {
    // --------------------
    // API
    // --------------------
    this.getMarketReferenceFee = () => __async(this, null, function* () {
      const data = (yield fetch("https://cache.jup.ag/reference-fees")).json();
      return data;
    });
    // --------------------
    // Helper methods
    // --------------------
    /**
     *
     * Code snippets from the Solana documentation
     * @see https://solana.com/developers/guides/advanced/how-to-request-optimal-compute#how-to-request-compute-budget
     */
    this.getSimulationUnits = (payload) => __async(this, null, function* () {
      const { instructions, payer, lookupTables } = payload;
      const testInstructions = [
        ComputeBudgetProgram.setComputeUnitLimit({ units: 14e5 }),
        ...instructions
      ];
      const testVersionedTxn = new VersionedTransaction(
        new TransactionMessage({
          instructions: testInstructions,
          payerKey: payer,
          recentBlockhash: PublicKey2.default.toString()
        }).compileToV0Message(lookupTables)
      );
      const simulation = yield this.connection.simulateTransaction(
        testVersionedTxn,
        {
          replaceRecentBlockhash: true,
          sigVerify: false
        }
      );
      if (simulation.value.err) {
        return void 0;
      }
      return simulation.value.unitsConsumed;
    });
    this.addMarginErrorForComputeUnitLimit = (units, margin) => Math.floor(units * margin);
    this.getReferralReferenceFeeInMicroLamports = () => __async(this, null, function* () {
      const marketReferenceFeeInLamports = yield this.getMarketReferenceFee();
      return marketReferenceFeeInLamports.referral;
    });
    // --------------------
    // Main methods
    // --------------------
    this.getOptimalComputeUnitLimitAndPrice = (payload) => __async(this, null, function* () {
      const simulationUnits = yield this.getSimulationUnits(payload);
      const simulationUnitsWithMarginError = this.addMarginErrorForComputeUnitLimit(simulationUnits, 1.2);
      const referenceFeeInMicroLamports = yield this.getReferralReferenceFeeInMicroLamports();
      return {
        // `computeUnitLimit`
        units: simulationUnitsWithMarginError,
        // `computeUnitPrice`
        microLamports: referenceFeeInMicroLamports
      };
    });
    this.connection = new Connection(RPC_URL);
  }
};
var feeService = new FeeServiceImpl();

// src/idl.ts
var IDL = {
  version: "0.1.0",
  name: "referral",
  instructions: [
    {
      name: "initializeProject",
      accounts: [
        {
          name: "payer",
          isMut: true,
          isSigner: true
        },
        {
          name: "base",
          isMut: false,
          isSigner: true
        },
        {
          name: "admin",
          isMut: false,
          isSigner: false
        },
        {
          name: "project",
          isMut: true,
          isSigner: false
        },
        {
          name: "systemProgram",
          isMut: false,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "InitializeProjectParams"
          }
        }
      ]
    },
    {
      name: "initializeReferralAccount",
      accounts: [
        {
          name: "payer",
          isMut: true,
          isSigner: true
        },
        {
          name: "partner",
          isMut: false,
          isSigner: false
        },
        {
          name: "project",
          isMut: false,
          isSigner: false
        },
        {
          name: "referralAccount",
          isMut: true,
          isSigner: true
        },
        {
          name: "systemProgram",
          isMut: false,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "InitializeReferralAccountParams"
          }
        }
      ]
    },
    {
      name: "initializeReferralAccountWithName",
      accounts: [
        {
          name: "payer",
          isMut: true,
          isSigner: true
        },
        {
          name: "partner",
          isMut: false,
          isSigner: false
        },
        {
          name: "project",
          isMut: false,
          isSigner: false
        },
        {
          name: "referralAccount",
          isMut: true,
          isSigner: false
        },
        {
          name: "systemProgram",
          isMut: false,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "InitializeReferralAccountWithNameParams"
          }
        }
      ]
    },
    {
      name: "updateProject",
      accounts: [
        {
          name: "admin",
          isMut: false,
          isSigner: true
        },
        {
          name: "project",
          isMut: true,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "UpdateProjectParams"
          }
        }
      ]
    },
    {
      name: "transferProject",
      accounts: [
        {
          name: "admin",
          isMut: false,
          isSigner: true
        },
        {
          name: "newAdmin",
          isMut: false,
          isSigner: false
        },
        {
          name: "project",
          isMut: true,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "TransferProjectParams"
          }
        }
      ]
    },
    {
      name: "updateReferralAccount",
      accounts: [
        {
          name: "admin",
          isMut: false,
          isSigner: true
        },
        {
          name: "project",
          isMut: false,
          isSigner: false
        },
        {
          name: "referralAccount",
          isMut: true,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "UpdateReferralAccountParams"
          }
        }
      ]
    },
    {
      name: "transferReferralAccount",
      accounts: [
        {
          name: "partner",
          isMut: false,
          isSigner: true
        },
        {
          name: "newPartner",
          isMut: false,
          isSigner: false
        },
        {
          name: "referralAccount",
          isMut: true,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "TransferReferralAccountParams"
          }
        }
      ]
    },
    {
      name: "initializeReferralTokenAccount",
      accounts: [
        {
          name: "payer",
          isMut: true,
          isSigner: true
        },
        {
          name: "project",
          isMut: false,
          isSigner: false
        },
        {
          name: "referralAccount",
          isMut: false,
          isSigner: false
        },
        {
          name: "referralTokenAccount",
          isMut: true,
          isSigner: false
        },
        {
          name: "mint",
          isMut: false,
          isSigner: false
        },
        {
          name: "systemProgram",
          isMut: false,
          isSigner: false
        },
        {
          name: "tokenProgram",
          isMut: false,
          isSigner: false
        }
      ],
      args: []
    },
    {
      name: "claim",
      accounts: [
        {
          name: "payer",
          isMut: true,
          isSigner: true
        },
        {
          name: "project",
          isMut: false,
          isSigner: false
        },
        {
          name: "admin",
          isMut: false,
          isSigner: false
        },
        {
          name: "projectAdminTokenAccount",
          isMut: true,
          isSigner: false
        },
        {
          name: "referralAccount",
          isMut: false,
          isSigner: false
        },
        {
          name: "referralTokenAccount",
          isMut: true,
          isSigner: false
        },
        {
          name: "partner",
          isMut: false,
          isSigner: false
        },
        {
          name: "partnerTokenAccount",
          isMut: true,
          isSigner: false
        },
        {
          name: "mint",
          isMut: false,
          isSigner: false
        },
        {
          name: "associatedTokenProgram",
          isMut: false,
          isSigner: false
        },
        {
          name: "systemProgram",
          isMut: false,
          isSigner: false
        },
        {
          name: "tokenProgram",
          isMut: false,
          isSigner: false
        }
      ],
      args: []
    },
    {
      name: "createAdminTokenAccount",
      accounts: [
        {
          name: "project",
          isMut: false,
          isSigner: false
        },
        {
          name: "projectAuthority",
          isMut: true,
          isSigner: false
        },
        {
          name: "admin",
          isMut: false,
          isSigner: false
        },
        {
          name: "projectAdminTokenAccount",
          isMut: true,
          isSigner: false
        },
        {
          name: "mint",
          isMut: false,
          isSigner: false
        },
        {
          name: "systemProgram",
          isMut: false,
          isSigner: false
        },
        {
          name: "tokenProgram",
          isMut: false,
          isSigner: false
        },
        {
          name: "associatedTokenProgram",
          isMut: false,
          isSigner: false
        }
      ],
      args: []
    },
    {
      name: "withdrawFromProject",
      accounts: [
        {
          name: "admin",
          isMut: true,
          isSigner: true
        },
        {
          name: "project",
          isMut: false,
          isSigner: false
        },
        {
          name: "projectAuthority",
          isMut: true,
          isSigner: false
        },
        {
          name: "systemProgram",
          isMut: false,
          isSigner: false
        }
      ],
      args: [
        {
          name: "params",
          type: {
            defined: "WithdrawFromProjectParams"
          }
        }
      ]
    }
  ],
  accounts: [
    {
      name: "project",
      type: {
        kind: "struct",
        fields: [
          {
            name: "base",
            type: "publicKey"
          },
          {
            name: "admin",
            type: "publicKey"
          },
          {
            name: "name",
            type: "string"
          },
          {
            name: "defaultShareBps",
            type: "u16"
          }
        ]
      }
    },
    {
      name: "referralAccount",
      type: {
        kind: "struct",
        fields: [
          {
            name: "partner",
            type: "publicKey"
          },
          {
            name: "project",
            type: "publicKey"
          },
          {
            name: "shareBps",
            type: "u16"
          },
          {
            name: "name",
            type: {
              option: "string"
            }
          }
        ]
      }
    }
  ],
  types: [
    {
      name: "InitializeProjectParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "name",
            type: "string"
          },
          {
            name: "defaultShareBps",
            type: "u16"
          }
        ]
      }
    },
    {
      name: "InitializeReferralAccountWithNameParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "name",
            type: "string"
          }
        ]
      }
    },
    {
      name: "InitializeReferralAccountParams",
      type: {
        kind: "struct",
        fields: []
      }
    },
    {
      name: "TransferProjectParams",
      type: {
        kind: "struct",
        fields: []
      }
    },
    {
      name: "TransferReferralAccountParams",
      type: {
        kind: "struct",
        fields: []
      }
    },
    {
      name: "UpdateProjectParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "name",
            type: {
              option: "string"
            }
          },
          {
            name: "defaultShareBps",
            type: {
              option: "u16"
            }
          }
        ]
      }
    },
    {
      name: "UpdateReferralAccountParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "shareBps",
            type: "u16"
          }
        ]
      }
    },
    {
      name: "WithdrawFromProjectParams",
      type: {
        kind: "struct",
        fields: [
          {
            name: "amount",
            type: "u64"
          }
        ]
      }
    }
  ],
  events: [
    {
      name: "InitializeProjectEvent",
      fields: [
        {
          name: "project",
          type: "publicKey",
          index: false
        },
        {
          name: "admin",
          type: "publicKey",
          index: false
        },
        {
          name: "name",
          type: "string",
          index: false
        },
        {
          name: "defaultShareBps",
          type: "u16",
          index: false
        }
      ]
    },
    {
      name: "UpdateProjectEvent",
      fields: [
        {
          name: "project",
          type: "publicKey",
          index: false
        },
        {
          name: "name",
          type: "string",
          index: false
        },
        {
          name: "defaultShareBps",
          type: "u16",
          index: false
        }
      ]
    },
    {
      name: "InitializeReferralAccountEvent",
      fields: [
        {
          name: "project",
          type: "publicKey",
          index: false
        },
        {
          name: "partner",
          type: "publicKey",
          index: false
        },
        {
          name: "referralAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "shareBps",
          type: "u16",
          index: false
        },
        {
          name: "name",
          type: {
            option: "string"
          },
          index: false
        }
      ]
    },
    {
      name: "UpdateReferralAccountEvent",
      fields: [
        {
          name: "referralAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "shareBps",
          type: "u16",
          index: false
        }
      ]
    },
    {
      name: "InitializeReferralTokenAccountEvent",
      fields: [
        {
          name: "project",
          type: "publicKey",
          index: false
        },
        {
          name: "referralAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "referralTokenAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "mint",
          type: "publicKey",
          index: false
        }
      ]
    },
    {
      name: "ClaimEvent",
      fields: [
        {
          name: "project",
          type: "publicKey",
          index: false
        },
        {
          name: "projectAdminTokenAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "referralAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "referralTokenAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "partnerTokenAccount",
          type: "publicKey",
          index: false
        },
        {
          name: "mint",
          type: "publicKey",
          index: false
        },
        {
          name: "referralAmount",
          type: "u64",
          index: false
        },
        {
          name: "projectAmount",
          type: "u64",
          index: false
        }
      ]
    }
  ],
  errors: [
    {
      code: 6e3,
      name: "InvalidCalculation"
    },
    {
      code: 6001,
      name: "InvalidSharePercentage"
    },
    {
      code: 6002,
      name: "NameTooLong"
    }
  ]
};

// src/utils.ts
import {
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddressSync,
  TOKEN_PROGRAM_ID
} from "@solana/spl-token";
var getOrCreateATAInstruction = (_0, _1, _2, ..._3) => __async(void 0, [_0, _1, _2, ..._3], function* (tokenMint, owner, connection, payer = owner, allowOwnerOffCurve = true, tokenProgram = TOKEN_PROGRAM_ID) {
  let toAccount;
  try {
    toAccount = getAssociatedTokenAddressSync(
      tokenMint,
      owner,
      allowOwnerOffCurve,
      tokenProgram
    );
    const account = yield connection.getAccountInfo(toAccount);
    if (!account) {
      const ix = createAssociatedTokenAccountInstruction(
        payer,
        toAccount,
        owner,
        tokenMint,
        tokenProgram
      );
      return [toAccount, ix];
    }
    return [toAccount, void 0];
  } catch (e) {
    console.error("Error::getOrCreateATAInstruction", e);
    throw e;
  }
});

// src/referral.ts
var useReferral = (connection) => {
  return new ReferralProvider(connection);
};
var ReferralProvider = class {
  constructor(connection) {
    this.connection = connection;
    const provider = new AnchorProvider(
      connection,
      {},
      AnchorProvider.defaultOptions()
    );
    this.program = new Program(IDL, PROGRAM_ID, provider);
  }
  getProjects() {
    return __async(this, arguments, function* (filters = []) {
      return yield this.program.account.project.all(filters);
    });
  }
  getProject(pubkey) {
    return __async(this, null, function* () {
      return yield this.program.account.project.fetch(pubkey);
    });
  }
  getReferralAccount(pubkey) {
    return __async(this, null, function* () {
      return yield this.program.account.referralAccount.fetch(pubkey);
    });
  }
  getReferralAccounts() {
    return __async(this, arguments, function* (filters = []) {
      return yield this.program.account.referralAccount.all(filters);
    });
  }
  getProjectAuthorityPubKey(project) {
    let [projectAuthority] = PublicKey3.findProgramAddressSync(
      [Buffer.from("project_authority"), project.base.toBuffer()],
      this.program.programId
    );
    return projectAuthority;
  }
  getReferralAccountWithNamePubKey({
    projectPubKey,
    name
  }) {
    const [referralAccountPubKey] = PublicKey3.findProgramAddressSync(
      [Buffer.from("referral"), projectPubKey.toBuffer(), Buffer.from(name)],
      this.program.programId
    );
    return referralAccountPubKey;
  }
  getReferralTokenAccountPubKey({
    referralAccountPubKey,
    mint
  }) {
    const [referralTokenAccountPubKey] = PublicKey3.findProgramAddressSync(
      [
        Buffer.from("referral_ata"),
        referralAccountPubKey.toBuffer(),
        mint.toBuffer()
      ],
      this.program.programId
    );
    return referralTokenAccountPubKey;
  }
  getReferralTokenAccounts(referralAccountAddress) {
    return __async(this, null, function* () {
      const referralAccount = yield this.program.account.referralAccount.fetch(
        new PublicKey3(referralAccountAddress)
      );
      const [tokenAccounts, token2022Accounts] = yield Promise.all(
        [TOKEN_PROGRAM_ID2, TOKEN_2022_PROGRAM_ID].map((programId) => __async(this, null, function* () {
          const mintSet = /* @__PURE__ */ new Set();
          const possibleTokenAccountSet = /* @__PURE__ */ new Set();
          const tokenAccountMap = /* @__PURE__ */ new Map();
          const allTokenAccounts = yield this.connection.getTokenAccountsByOwner(
            referralAccount.project,
            { programId }
          );
          allTokenAccounts.value.map((tokenAccount) => {
            const accountData = AccountLayout.decode(tokenAccount.account.data);
            if (!mintSet.has(accountData.mint.toBase58())) {
              const address = this.getReferralTokenAccountPubKey({
                referralAccountPubKey: new PublicKey3(referralAccountAddress),
                mint: accountData.mint
              });
              mintSet.add(accountData.mint.toBase58());
              possibleTokenAccountSet.add(address.toBase58());
            }
            tokenAccountMap.set(tokenAccount.pubkey.toBase58(), accountData);
          });
          return Array.from(possibleTokenAccountSet).reduce((acc, address) => {
            const tokenAccount = tokenAccountMap.get(address);
            if (tokenAccount) {
              acc.push({ pubkey: new PublicKey3(address), account: tokenAccount });
            }
            return acc;
          }, new Array());
        }))
      );
      return { tokenAccounts, token2022Accounts };
    });
  }
  getReferralTokenAccountsWithStrategy(_0) {
    return __async(this, arguments, function* (referralAccountAddress, strategy = {
      type: "top-tokens",
      topN: 100
    }) {
      const tokens = yield (() => __async(this, null, function* () {
        if (strategy.type === "top-tokens") {
          const topTokens = (yield (yield fetch("https://cache.jup.ag/top-tokens")).json()).slice(0, strategy.topN);
          return topTokens;
        } else if (strategy.type === "token-list") {
          const tokens2 = (yield (yield fetch(`https://token.jup.ag/${strategy.tokenList}`)).json()).map(({ address }) => address);
          return tokens2;
        } else {
          throw new Error("Invalid strategy");
        }
      }))();
      const referralTokenAccounts = tokens.map(
        (topToken) => this.getReferralTokenAccountPubKey({
          referralAccountPubKey: new PublicKey3(referralAccountAddress),
          mint: new PublicKey3(topToken)
        })
      );
      const tokenAccounts = [];
      const token2022Accounts = [];
      const accountInfos = yield chunkedGetMultipleAccountInfos(
        this.connection,
        referralTokenAccounts
      );
      for (const [index, accountInfo] of accountInfos.entries()) {
        if (!accountInfo)
          continue;
        const address = referralTokenAccounts[index];
        const rawAccount = AccountLayout.decode(accountInfo.data);
        const rawAccountWithPubkey = {
          pubkey: address,
          account: rawAccount
        };
        if (accountInfo.owner.equals(TOKEN_PROGRAM_ID2)) {
          tokenAccounts.push(rawAccountWithPubkey);
        } else if (accountInfo.owner.equals(TOKEN_2022_PROGRAM_ID)) {
          token2022Accounts.push(rawAccountWithPubkey);
        }
      }
      return {
        tokenAccounts,
        token2022Accounts
      };
    });
  }
  initializeProject(_0) {
    return __async(this, arguments, function* ({
      basePubKey,
      adminPubKey,
      name,
      defaultShareBps
    }) {
      const [projectPubKey] = PublicKey3.findProgramAddressSync(
        [Buffer.from("project"), basePubKey.toBuffer()],
        this.program.programId
      );
      return yield this.program.methods.initializeProject({ name, defaultShareBps }).accounts({
        admin: adminPubKey,
        project: projectPubKey,
        base: basePubKey
      }).transaction();
    });
  }
  transferProject(_0) {
    return __async(this, arguments, function* ({
      newAdminPubKey,
      projectPubKey
    }) {
      const project = yield this.program.account.project.fetch(projectPubKey);
      return yield this.program.methods.transferProject({}).accounts({
        admin: project.admin,
        project: projectPubKey,
        newAdmin: newAdminPubKey
      }).transaction();
    });
  }
  initializeReferralAccount(_0) {
    return __async(this, arguments, function* ({
      projectPubKey,
      partnerPubKey,
      payerPubKey,
      referralAccountPubKey
    }) {
      return yield this.program.methods.initializeReferralAccount({}).accounts({
        project: projectPubKey,
        partner: partnerPubKey,
        referralAccount: referralAccountPubKey,
        payer: payerPubKey
      }).transaction();
    });
  }
  initializeReferralAccountWithName(_0) {
    return __async(this, arguments, function* ({
      projectPubKey,
      partnerPubKey,
      payerPubKey,
      name
    }) {
      const referralAccountPubKey = this.getReferralAccountWithNamePubKey({
        projectPubKey,
        name
      });
      const tx = yield this.program.methods.initializeReferralAccountWithName({ name }).accounts({
        project: projectPubKey,
        partner: partnerPubKey,
        referralAccount: referralAccountPubKey,
        payer: payerPubKey
      }).transaction();
      return { tx, referralAccountPubKey };
    });
  }
  transferReferralAccount(_0) {
    return __async(this, arguments, function* ({
      newPartnerPubKey,
      referralAccountPubKey
    }) {
      const referralAccount = yield this.program.account.referralAccount.fetch(
        referralAccountPubKey
      );
      return yield this.program.methods.transferReferralAccount({}).accounts({
        partner: referralAccount.partner,
        newPartner: newPartnerPubKey,
        referralAccount: referralAccountPubKey
      }).transaction();
    });
  }
  initializeReferralTokenAccount(_0) {
    return __async(this, arguments, function* ({
      payerPubKey,
      referralAccountPubKey,
      mint
    }) {
      const mintAccount = yield this.connection.getAccountInfo(mint);
      if (!mintAccount)
        throw new Error("Invalid mint");
      if (![TOKEN_PROGRAM_ID2, TOKEN_2022_PROGRAM_ID].some(
        (id) => id.equals(mintAccount.owner)
      ))
        throw new Error("Invalid mint");
      const referralAccount = yield this.program.account.referralAccount.fetch(
        referralAccountPubKey
      );
      const referralTokenAccountPubKey = this.getReferralTokenAccountPubKey({
        referralAccountPubKey,
        mint
      });
      const tx = yield this.program.methods.initializeReferralTokenAccount().accounts({
        payer: payerPubKey,
        project: referralAccount.project,
        referralAccount: referralAccountPubKey,
        referralTokenAccount: referralTokenAccountPubKey,
        mint,
        tokenProgram: mintAccount.owner
      }).transaction();
      return { tx, referralTokenAccountPubKey };
    });
  }
  claim(_0) {
    return __async(this, arguments, function* ({
      payerPubKey,
      referralAccountPubKey,
      mint
    }) {
      const mintAccount = yield this.connection.getAccountInfo(mint);
      if (!mintAccount)
        throw new Error("Invalid mint");
      if (![TOKEN_PROGRAM_ID2, TOKEN_2022_PROGRAM_ID].some(
        (id) => id.equals(mintAccount.owner)
      ))
        throw new Error("Invalid mint");
      const referralAccount = yield this.program.account.referralAccount.fetch(
        referralAccountPubKey
      );
      const project = yield this.program.account.project.fetch(
        referralAccount.project
      );
      const [
        referralTokenAccountPubKey,
        [partnerTokenAccount, createPartnerTokenAccountIx],
        [projectAdminTokenAccount, createProjectAdminTokenAccountIx]
      ] = yield Promise.all([
        this.getReferralTokenAccountPubKey({
          referralAccountPubKey,
          mint
        }),
        getOrCreateATAInstruction(
          mint,
          referralAccount.partner,
          this.connection,
          payerPubKey,
          void 0,
          mintAccount.owner
        ),
        getOrCreateATAInstruction(
          mint,
          project.admin,
          this.connection,
          payerPubKey,
          void 0,
          mintAccount.owner
        )
      ]);
      let preInstructions = [];
      if (createPartnerTokenAccountIx)
        preInstructions.push(createPartnerTokenAccountIx);
      if (createProjectAdminTokenAccountIx) {
        const projectAuthority = this.getProjectAuthorityPubKey(project);
        const ix = yield this.program.methods.createAdminTokenAccount().accounts({
          project: referralAccount.project,
          projectAuthority,
          admin: project.admin,
          projectAdminTokenAccount,
          mint,
          tokenProgram: mintAccount.owner
        }).instruction();
        preInstructions.push(ix);
      }
      const transaction = yield this.program.methods.claim().accounts({
        payer: payerPubKey,
        project: referralAccount.project,
        admin: project.admin,
        projectAdminTokenAccount,
        referralAccount: referralAccountPubKey,
        referralTokenAccount: referralTokenAccountPubKey,
        partner: referralAccount.partner,
        partnerTokenAccount,
        mint,
        tokenProgram: mintAccount.owner
      }).preInstructions(preInstructions).transaction();
      const instructions = transaction.instructions;
      const addressLookupTable = yield this.connection.getAddressLookupTable(
        new PublicKey3("GBzQG2iFrPwXjGtCnwNt9S5eHd8xAR8jUMt3QDJpnjud")
      );
      const lookupTableAccount = addressLookupTable.value;
      const { units, microLamports } = yield feeService.getOptimalComputeUnitLimitAndPrice({
        instructions: transaction.instructions,
        payer: payerPubKey,
        lookupTables: [lookupTableAccount]
      });
      instructions.unshift(
        ComputeBudgetProgram2.setComputeUnitPrice({ microLamports })
      );
      if (units) {
        instructions.unshift(ComputeBudgetProgram2.setComputeUnitLimit({ units }));
      }
      const blockhash = (yield this.connection.getLatestBlockhash()).blockhash;
      const messageV0 = new TransactionMessage2({
        payerKey: payerPubKey,
        instructions,
        recentBlockhash: blockhash
      }).compileToV0Message();
      return new VersionedTransaction2(messageV0);
    });
  }
  claimAll(_0) {
    return __async(this, arguments, function* ({
      payerPubKey,
      referralAccountPubKey,
      strategy
    }) {
      const blockhash = (yield this.connection.getLatestBlockhash()).blockhash;
      const lookupTableAccount = yield this.connection.getAddressLookupTable(
        new PublicKey3("GBzQG2iFrPwXjGtCnwNt9S5eHd8xAR8jUMt3QDJpnjud")
      ).then((res) => res.value);
      const referralAccount = yield this.program.account.referralAccount.fetch(
        referralAccountPubKey
      );
      const project = yield this.program.account.project.fetch(
        referralAccount.project
      );
      const projectAuthority = this.getProjectAuthorityPubKey(project);
      const { tokenAccounts, token2022Accounts } = strategy ? yield this.getReferralTokenAccountsWithStrategy(
        referralAccountPubKey.toString(),
        strategy
      ) : yield this.getReferralTokenAccounts(referralAccountPubKey.toString());
      const vtTxs = yield Promise.all(
        [tokenAccounts, token2022Accounts].map((accounts, idx) => __async(this, null, function* () {
          const tokenProgramId = idx === 0 ? TOKEN_PROGRAM_ID2 : TOKEN_2022_PROGRAM_ID;
          const tokensWithAmount = accounts.filter(
            (item) => item.account.amount > 0 && item.account.state === 1
          );
          const claimParams = yield Promise.all(
            tokensWithAmount.map((token) => __async(this, null, function* () {
              const referralTokenAccountPubKey = this.getReferralTokenAccountPubKey({
                referralAccountPubKey,
                mint: token.account.mint
              });
              const preInstructions = [];
              const partnerTokenAccount = getAssociatedTokenAddressSync2(
                token.account.mint,
                referralAccount.partner,
                true,
                tokenProgramId
              );
              preInstructions.push(
                createAssociatedTokenAccountIdempotentInstruction(
                  payerPubKey,
                  partnerTokenAccount,
                  referralAccount.partner,
                  token.account.mint,
                  tokenProgramId
                )
              );
              const projectAdminTokenAccount = getAssociatedTokenAddressSync2(
                token.account.mint,
                project.admin,
                true,
                tokenProgramId
              );
              const ix = yield this.program.methods.createAdminTokenAccount().accounts({
                project: referralAccount.project,
                projectAuthority,
                admin: project.admin,
                projectAdminTokenAccount,
                mint: token.account.mint,
                tokenProgram: tokenProgramId
              }).instruction();
              preInstructions.push(ix);
              return {
                referralTokenAccountPubKey,
                projectAdminTokenAccount,
                partnerTokenAccount,
                preInstructions,
                mint: token.account.mint
              };
            }))
          );
          const txs = [];
          let instructions = [];
          let chunk2 = 0;
          for (const {
            referralTokenAccountPubKey,
            projectAdminTokenAccount,
            partnerTokenAccount,
            mint,
            preInstructions
          } of claimParams) {
            const tx = yield this.program.methods.claim().accounts({
              payer: payerPubKey,
              project: referralAccount.project,
              admin: project.admin,
              projectAdminTokenAccount,
              referralAccount: referralAccountPubKey,
              referralTokenAccount: referralTokenAccountPubKey,
              partner: referralAccount.partner,
              partnerTokenAccount,
              mint,
              tokenProgram: tokenProgramId
            }).preInstructions(preInstructions).transaction();
            instructions.push(...tx.instructions);
            chunk2 += 1;
            if (chunk2 === 4) {
              const { units, microLamports } = yield feeService.getOptimalComputeUnitLimitAndPrice({
                instructions,
                payer: payerPubKey,
                lookupTables: [lookupTableAccount]
              });
              instructions.unshift(
                ComputeBudgetProgram2.setComputeUnitPrice({ microLamports })
              );
              if (units) {
                instructions.unshift(
                  ComputeBudgetProgram2.setComputeUnitLimit({ units })
                );
              }
              const messageV0 = new TransactionMessage2({
                payerKey: payerPubKey,
                instructions,
                recentBlockhash: blockhash
              }).compileToV0Message([lookupTableAccount]);
              chunk2 = 0;
              instructions = [];
              txs.push(new VersionedTransaction2(messageV0));
            }
          }
          return txs;
        }))
      );
      return vtTxs.flat();
    });
  }
  claimPartially(_0) {
    return __async(this, arguments, function* ({
      payerPubKey,
      referralAccountPubKey,
      withdrawalableTokenAddress
    }) {
      const blockhash = (yield this.connection.getLatestBlockhash()).blockhash;
      const lookupTableAccount = yield this.connection.getAddressLookupTable(
        new PublicKey3("GBzQG2iFrPwXjGtCnwNt9S5eHd8xAR8jUMt3QDJpnjud")
      ).then((res) => res.value);
      const referralAccount = yield this.program.account.referralAccount.fetch(
        referralAccountPubKey
      );
      const project = yield this.program.account.project.fetch(
        referralAccount.project
      );
      const projectAuthority = this.getProjectAuthorityPubKey(project);
      const result = yield this.connection.getMultipleAccountsInfo(
        withdrawalableTokenAddress
      );
      const claimInstructionParams = yield Promise.all(
        result.map((item) => __async(this, null, function* () {
          const tokenProgramId = item.owner;
          const tokenAccountData = AccountLayout.decode(item.data);
          const referralTokenAccountPubKey = this.getReferralTokenAccountPubKey({
            referralAccountPubKey,
            mint: tokenAccountData.mint
          });
          const preInstructions = [];
          const partnerTokenAccount = getAssociatedTokenAddressSync2(
            tokenAccountData.mint,
            referralAccount.partner,
            true,
            tokenProgramId
          );
          preInstructions.push(
            createAssociatedTokenAccountIdempotentInstruction(
              payerPubKey,
              partnerTokenAccount,
              referralAccount.partner,
              tokenAccountData.mint,
              tokenProgramId
            )
          );
          const projectAdminTokenAccount = getAssociatedTokenAddressSync2(
            tokenAccountData.mint,
            project.admin,
            true,
            tokenProgramId
          );
          const ix = yield this.program.methods.createAdminTokenAccount().accounts({
            project: referralAccount.project,
            projectAuthority,
            admin: project.admin,
            projectAdminTokenAccount,
            mint: tokenAccountData.mint,
            tokenProgram: tokenProgramId
          }).instruction();
          preInstructions.push(ix);
          return {
            referralTokenAccountPubKey,
            projectAdminTokenAccount,
            partnerTokenAccount,
            preInstructions,
            tokenProgramId,
            mint: tokenAccountData.mint
          };
        }))
      );
      const chunkedInstructions = chunk(claimInstructionParams, 4);
      const txs = yield Promise.all(
        chunkedInstructions.map((chunkParams) => __async(this, null, function* () {
          let instructions = [];
          yield Promise.all(
            chunkParams.map(
              (_02) => __async(this, [_02], function* ({
                referralTokenAccountPubKey,
                projectAdminTokenAccount,
                partnerTokenAccount,
                mint,
                preInstructions,
                tokenProgramId
              }) {
                const tx = yield this.program.methods.claim().accounts({
                  payer: payerPubKey,
                  project: referralAccount.project,
                  admin: project.admin,
                  projectAdminTokenAccount,
                  referralAccount: referralAccountPubKey,
                  referralTokenAccount: referralTokenAccountPubKey,
                  partner: referralAccount.partner,
                  partnerTokenAccount,
                  mint,
                  tokenProgram: tokenProgramId
                }).preInstructions(preInstructions).transaction();
                instructions.push(...tx.instructions);
              })
            )
          );
          const { units, microLamports } = yield feeService.getOptimalComputeUnitLimitAndPrice({
            instructions,
            payer: payerPubKey,
            lookupTables: [lookupTableAccount]
          });
          instructions.unshift(
            ComputeBudgetProgram2.setComputeUnitPrice({ microLamports })
          );
          if (units) {
            instructions.unshift(
              ComputeBudgetProgram2.setComputeUnitLimit({ units })
            );
          }
          const messageV0 = new TransactionMessage2({
            payerKey: payerPubKey,
            instructions,
            recentBlockhash: blockhash
          }).compileToV0Message([lookupTableAccount]);
          instructions = [];
          return new VersionedTransaction2(messageV0);
        }))
      );
      return txs;
    });
  }
};

// src/filter.ts
var projectAdminFilter = (publicKey) => {
  return {
    memcmp: {
      offset: 8 + 32,
      bytes: publicKey.toBase58()
    }
  };
};
var referralAccountPartnerFilter = (publicKey) => {
  return {
    memcmp: {
      offset: 8,
      bytes: publicKey.toBase58()
    }
  };
};
var referralAccountProjectFilter = (publicKey) => {
  return {
    memcmp: {
      offset: 8 + 32,
      bytes: publicKey.toBase58()
    }
  };
};
export {
  IDL,
  PROGRAM_ID,
  RPC_URL,
  ReferralProvider,
  projectAdminFilter,
  referralAccountPartnerFilter,
  referralAccountProjectFilter,
  useReferral
};
//# sourceMappingURL=index.mjs.map